import React, { useState, useEffect } from 'react';
import api from '../config/api';
import FieldMappingBuilder from './FieldMappingBuilder';

function CompletionAgentConfiguration() {
  const [agents, setAgents] = useState([]);
  const [inventoryTypes, setInventoryTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);

  useEffect(() => {
    fetchAgents();
    fetchInventoryTypes();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await api.get('/completion-agents');
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching completion agents:', error);
      setError('Failed to load completion agents');
    } finally {
      setLoading(false);
    }
  };

  const fetchInventoryTypes = async () => {
    try {
      const response = await api.get('/inventory-types');
      setInventoryTypes(response.data);
    } catch (error) {
      console.error('Error fetching inventory types:', error);
    }
  };

  const handleCreateAgent = () => {
    setEditingAgent(null);
    setShowModal(true);
  };

  const handleEditAgent = (agent) => {
    setEditingAgent(agent);
    setShowModal(true);
  };

  const handleDeleteAgent = async (id) => {
    if (!window.confirm('Are you sure you want to delete this completion agent?')) {
      return;
    }

    try {
      await api.delete(`/completion-agents/${id}`);
      fetchAgents();
    } catch (error) {
      console.error('Error deleting completion agent:', error);
      setError('Failed to delete completion agent');
    }
  };

  const handleDuplicateAgent = async (id) => {
    try {
      await api.post(`/completion-agents/${id}/duplicate`);
      fetchAgents();
    } catch (error) {
      console.error('Error duplicating completion agent:', error);
      setError('Failed to duplicate completion agent');
    }
  };

  const handleTestAgent = async (id) => {
    try {
      const response = await api.get(`/completion-agents/${id}/test`);
      if (response.data.success) {
        alert('✅ Connection test successful!');
      } else {
        alert('❌ Connection test failed: ' + response.data.error);
      }
    } catch (error) {
      console.error('Error testing completion agent:', error);
      alert('❌ Connection test failed: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingAgent(null);
    fetchAgents();
  };

  if (loading) {
    return <div>Loading completion agents...</div>;
  }

  return (
    <div className="completion-agent-configuration">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2>Completion Agent Configuration</h2>
        <button className="btn" onClick={handleCreateAgent}>
          Add Completion Agent
        </button>
      </div>

      {error && (
        <div style={{ 
          color: '#e74c3c', 
          backgroundColor: '#fdf2f2', 
          padding: '1rem', 
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      <div className="agents-list">
        {agents.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '2rem', 
            color: '#666',
            backgroundColor: '#f9f9f9',
            borderRadius: '4px'
          }}>
            <p>No completion agents configured yet.</p>
            <p>Create your first completion agent to automatically fill item fields using external APIs.</p>
          </div>
        ) : (
          agents.map((agent) => (
            <div key={agent.id} className="agent-card" style={{
              border: '1px solid #ddd',
              borderRadius: '8px',
              padding: '1.5rem',
              marginBottom: '1rem',
              backgroundColor: '#fff'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {agent.name}
                    {agent.is_active ? (
                      <span style={{ 
                        backgroundColor: '#27ae60', 
                        color: 'white', 
                        padding: '0.2rem 0.5rem', 
                        borderRadius: '12px', 
                        fontSize: '0.7rem' 
                      }}>
                        ACTIVE
                      </span>
                    ) : (
                      <span style={{ 
                        backgroundColor: '#95a5a6', 
                        color: 'white', 
                        padding: '0.2rem 0.5rem', 
                        borderRadius: '12px', 
                        fontSize: '0.7rem' 
                      }}>
                        INACTIVE
                      </span>
                    )}
                  </h3>
                  {agent.description && (
                    <p style={{ margin: '0 0 1rem 0', color: '#666' }}>{agent.description}</p>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                    <div>
                      <strong>Inventory Type:</strong><br />
                      <span style={{ fontSize: '0.9rem', color: '#555' }}>
                        {agent.inventory_type_name || 'Unknown'}
                      </span>
                    </div>
                    <div>
                      <strong>API URL:</strong><br />
                      <span style={{ fontSize: '0.9rem', color: '#555', wordBreak: 'break-all' }}>
                        {agent.api_url}
                      </span>
                    </div>
                    <div>
                      <strong>Search Field:</strong><br />
                      <span style={{ fontSize: '0.9rem', color: '#555' }}>
                        {agent.search_field}
                      </span>
                    </div>
                    <div>
                      <strong>Method:</strong><br />
                      <span style={{ fontSize: '0.9rem', color: '#555' }}>
                        {agent.request_method}
                      </span>
                    </div>
                    <div>
                      <strong>API Key:</strong><br />
                      <span style={{ fontSize: '0.9rem', color: '#555' }}>
                        {agent.has_api_key ? agent.api_key_masked : 'Not configured'}
                      </span>
                    </div>
                  </div>
                  {Object.keys(agent.field_mappings || {}).length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                      <strong>Field Mappings:</strong><br />
                      <div style={{ fontSize: '0.9rem', color: '#555', marginTop: '0.5rem' }}>
                        {Object.entries(agent.field_mappings).map(([field, path]) => (
                          <div key={field} style={{ marginBottom: '0.25rem' }}>
                            <code>{field}</code> ← <code>{path}</code>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => handleTestAgent(agent.id)}
                    style={{ fontSize: '0.8rem', padding: '0.5rem 1rem' }}
                  >
                    Test Connection
                  </button>
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => handleEditAgent(agent)}
                    style={{ fontSize: '0.8rem', padding: '0.5rem 1rem' }}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => handleDuplicateAgent(agent.id)}
                    style={{ fontSize: '0.8rem', padding: '0.5rem 1rem' }}
                  >
                    Duplicate
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={() => handleDeleteAgent(agent.id)}
                    style={{ fontSize: '0.8rem', padding: '0.5rem 1rem' }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <CompletionAgentModal 
          agent={editingAgent}
          inventoryTypes={inventoryTypes}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
}

function CompletionAgentModal({ agent, inventoryTypes, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    inventory_type_id: '',
    api_url: '',
    api_key: '',
    request_method: 'GET',
    request_headers: {},
    request_body_template: '',
    response_path: '',
    multiple_results_path: '',
    result_display_field: '',
    field_mappings: {},
    search_field: '',
    is_active: true
  });
  const [selectedInventoryType, setSelectedInventoryType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sampleResponse, setSampleResponse] = useState(null);
  const [availablePaths, setAvailablePaths] = useState([]);
  const [fetchingSample, setFetchingSample] = useState(false);
  const [sampleSearchValue, setSampleSearchValue] = useState('test');

  // Helper function to safely parse fields
  const getInventoryTypeFields = (inventoryType) => {
    if (!inventoryType || !inventoryType.fields) return [];
    
    // If it's already an array, return it
    if (Array.isArray(inventoryType.fields)) {
      return inventoryType.fields;
    }
    
    // If it's a string, parse it
    if (typeof inventoryType.fields === 'string') {
      try {
        return JSON.parse(inventoryType.fields);
      } catch (e) {
        console.error('Error parsing inventory type fields:', e);
        return [];
      }
    }
    
    return [];
  };

  useEffect(() => {
    if (agent) {
      setFormData({
        name: agent.name || '',
        description: agent.description || '',
        inventory_type_id: agent.inventory_type_id || '',
        api_url: agent.api_url || '',
        api_key: '', // Don't prefill API key for security
        request_method: agent.request_method || 'GET',
        request_headers: agent.request_headers || {},
        request_body_template: agent.request_body_template || '',
        response_path: agent.response_path || '',
        multiple_results_path: agent.multiple_results_path || '',
        result_display_field: agent.result_display_field || '',
        field_mappings: agent.field_mappings || {},
        search_field: agent.search_field || '',
        is_active: agent.is_active !== undefined ? agent.is_active : true
      });
      
      // Find and set the selected inventory type
      const inventoryType = inventoryTypes.find(type => type.id === agent.inventory_type_id);
      setSelectedInventoryType(inventoryType || null);
    }
  }, [agent, inventoryTypes]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // If inventory type changed, update selected inventory type and clear field mappings
    if (name === 'inventory_type_id') {
      const inventoryType = inventoryTypes.find(type => type.id === parseInt(value));
      setSelectedInventoryType(inventoryType || null);
      setFormData(prev => ({
        ...prev,
        field_mappings: {}, // Clear existing mappings when inventory type changes
        search_field: '' // Clear search field as well
      }));
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (agent) {
        await api.put(`/completion-agents/${agent.id}`, formData);
      } else {
        await api.post('/completion-agents', formData);
      }
      onClose();
    } catch (error) {
      console.error('Error saving completion agent:', error);
      setError(error.response?.data?.error || 'Failed to save completion agent');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplate = (templateName) => {
    if (templateName === 'themoviedb') {
      setFormData(prev => ({
        ...prev,
        name: 'The Movie Database',
        description: 'Fetch movie information from TheMovieDB API',
        api_url: 'https://api.themoviedb.org/3/search/movie?query={search_value}',
        request_method: 'GET',
        request_headers: {
          'Authorization': 'Bearer {api_key}'
        },
        response_path: '',
        multiple_results_path: 'results',
        result_display_field: 'title',
        field_mappings: selectedInventoryType ? getDefaultMovieFieldMappings() : {},
        search_field: 'Title'
      }));
    }
  };

  const getDefaultMovieFieldMappings = () => {
    if (!selectedInventoryType) return {};
    
    const mappings = {};
    const fields = getInventoryTypeFields(selectedInventoryType);
    
    // Create default mappings for common movie fields
    fields.forEach(field => {
      const fieldName = field.name.toLowerCase();
      if (fieldName.includes('title') || fieldName.includes('name')) {
        mappings[field.name] = 'title';
      } else if (fieldName.includes('year') || fieldName.includes('date')) {
        mappings[field.name] = 'release_date';
      } else if (fieldName.includes('overview') || fieldName.includes('description') || fieldName.includes('summary')) {
        mappings[field.name] = 'overview';
      } else if (fieldName.includes('rating') || fieldName.includes('score')) {
        mappings[field.name] = 'vote_average';
      }
    });
    
    return mappings;
  };

  const fetchSampleResponse = async () => {
    if (!formData.api_url) {
      setError('Please enter an API URL first');
      return;
    }

    setFetchingSample(true);
    setError('');

    try {
      // For existing agents, use the agent's stored API key if no new one is provided
      let apiKeyToUse = formData.api_key;
      if (!apiKeyToUse && agent && agent.has_api_key) {
        // For existing agents with stored API keys, let the backend use the stored key
        apiKeyToUse = 'USE_STORED_KEY';
      }

      const response = await api.post('/completion-agents/sample-response', {
        api_url: formData.api_url,
        request_method: formData.request_method,
        request_headers: formData.request_headers,
        request_body_template: formData.request_body_template,
        api_key: apiKeyToUse,
        sample_search_value: sampleSearchValue,
        agent_id: agent ? agent.id : null // Send agent ID so backend can use stored key
      });

      if (response.data.success) {
        setSampleResponse(response.data.sample_response);
        setAvailablePaths(response.data.available_paths);
        
        // Clear existing mappings and response path when new sample is fetched
        setFormData(prev => ({
          ...prev,
          response_path: '',
          multiple_results_path: '',
          result_display_field: '',
          field_mappings: {}
        }));
      } else {
        setError('Failed to fetch sample response: ' + response.data.error);
      }
    } catch (error) {
      console.error('Error fetching sample response:', error);
      setError('Failed to fetch sample response: ' + (error.response?.data?.error || error.message));
    } finally {
      setFetchingSample(false);
    }
  };

  return (
    <div className="modal">
      <div className="modal-content" style={{ maxWidth: '800px', width: '90vw' }}>
        <h2>{agent ? 'Edit Completion Agent' : 'Create Completion Agent'}</h2>
        
        {!agent && (
          <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <strong>Quick Templates:</strong>
            <div style={{ marginTop: '0.5rem' }}>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => loadTemplate('themoviedb')}
                style={{ fontSize: '0.9rem', marginRight: '0.5rem' }}
              >
                TheMovieDB Template
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Agent Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="e.g., Movie Database API"
            />
          </div>

          <div className="form-group">
            <label>Inventory Type *</label>
            <select
              name="inventory_type_id"
              value={formData.inventory_type_id}
              onChange={handleInputChange}
              required
            >
              <option value="">-- Select inventory type --</option>
              {inventoryTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Search Field *</label>
            <select
              name="search_field"
              value={formData.search_field}
              onChange={handleInputChange}
              required
              disabled={!selectedInventoryType}
            >
              <option value="">-- Select search field --</option>
              {selectedInventoryType && getInventoryTypeFields(selectedInventoryType).map((field) => (
                <option key={field.name} value={field.name}>
                  {field.name} ({field.type})
                </option>
              ))}
            </select>
            {!selectedInventoryType && (
              <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                Select an inventory type first to see available fields
              </p>
            )}
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="2"
              placeholder="Describe what this completion agent does..."
            />
          </div>

          <div className="form-group">
            <label>API URL * <span style={{ fontSize: '0.8rem', color: '#666' }}>Use {'{search_value}'} placeholder</span></label>
            <input
              type="url"
              name="api_url"
              value={formData.api_url}
              onChange={handleInputChange}
              required
              placeholder="https://api.example.com/search?q={search_value}"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group">
              <label>Request Method</label>
              <select
                name="request_method"
                value={formData.request_method}
                onChange={handleInputChange}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
              </select>
            </div>
            <div className="form-group">
              <label>API Key</label>
              <input
                type="password"
                name="api_key"
                value={formData.api_key}
                onChange={handleInputChange}
                placeholder={agent ? "Enter new API key (optional)" : "Enter API key if required"}
              />
              {agent && agent.has_api_key && !formData.api_key && (
                <p style={{ fontSize: '0.8rem', color: '#27ae60', margin: '0.25rem 0 0 0' }}>
                  ✓ Using stored API key (***{agent.api_key_masked?.slice(-3) || '***'})
                </p>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>
              Response Path Configuration
              <span style={{ fontSize: '0.8rem', color: '#666', marginLeft: '1rem' }}>
                Fetch a sample response to see available paths
              </span>
            </label>
            
            <div style={{ 
              border: '1px solid #ddd', 
              borderRadius: '4px', 
              padding: '1rem',
              backgroundColor: '#f9f9f9'
            }}>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', alignItems: 'center' }}>
                <input
                  type="text"
                  value={sampleSearchValue}
                  onChange={(e) => setSampleSearchValue(e.target.value)}
                  placeholder="Sample search value (e.g., 'test', 'matrix')"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={fetchSampleResponse}
                  disabled={fetchingSample || !formData.api_url}
                  className="btn btn-secondary"
                  style={{ fontSize: '0.9rem' }}
                >
                  {fetchingSample ? '⏳ Fetching...' : '🔍 Fetch Sample'}
                </button>
              </div>
              
              {availablePaths.length > 0 && (
                <div>
                  <label style={{ fontSize: '0.9rem', marginBottom: '0.5rem', display: 'block' }}>
                    Response Path (select from available paths):
                  </label>
                  <select
                    name="response_path"
                    value={formData.response_path}
                    onChange={handleInputChange}
                    style={{ width: '100%', marginBottom: '0.5rem' }}
                  >
                    <option value="">-- Select response path --</option>
                    {availablePaths.map((pathInfo, index) => (
                      <option key={index} value={pathInfo.path}>
                        {pathInfo.path} ({pathInfo.type}) - {pathInfo.description}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {!sampleResponse && (
                <div>
                  <p style={{ fontSize: '0.8rem', color: '#666', fontStyle: 'italic', margin: '0 0 0.5rem 0' }}>
                    Enter your API details above, then fetch a sample response to see available data paths.
                  </p>
                  {formData.api_url.includes('themoviedb.org') && !formData.api_key && !(agent && agent.has_api_key) && (
                    <p style={{ fontSize: '0.8rem', color: '#e67e22', fontStyle: 'italic', margin: 0 }}>
                      ⚠️ TheMovieDB requires a valid API key. Get one at: https://developers.themoviedb.org/3/getting-started
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Multiple Results Configuration</label>
            <div style={{ 
              border: '1px solid #ddd', 
              borderRadius: '4px', 
              padding: '1rem',
              backgroundColor: '#f9f9f9'
            }}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ fontSize: '0.9rem', marginBottom: '0.5rem', display: 'block' }}>
                  Multiple Results Path (path to array of results):
                </label>
                {availablePaths.length > 0 ? (
                  <select
                    name="multiple_results_path"
                    value={formData.multiple_results_path}
                    onChange={handleInputChange}
                    style={{ width: '100%' }}
                  >
                    <option value="">-- No multiple results (single result only) --</option>
                    {availablePaths
                      .filter(pathInfo => pathInfo.type === 'array' && !pathInfo.path.includes('.*'))
                      .map((pathInfo, index) => (
                        <option key={index} value={pathInfo.path}>
                          {pathInfo.path} - {pathInfo.description}
                        </option>
                      ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    name="multiple_results_path"
                    value={formData.multiple_results_path}
                    onChange={handleInputChange}
                    placeholder="e.g., results (fetch sample to see array paths)"
                    style={{ width: '100%' }}
                  />
                )}
              </div>

              <div>
                <label style={{ fontSize: '0.9rem', marginBottom: '0.5rem', display: 'block' }}>
                  Result Display Field (field to show user for selection):
                </label>
                {availablePaths.length > 0 ? (
                  <select
                    name="result_display_field"
                    value={formData.result_display_field}
                    onChange={handleInputChange}
                    style={{ width: '100%' }}
                  >
                    <option value="">-- Use default numbering (Result 1, Result 2, etc.) --</option>
                    {availablePaths
                      .filter(pathInfo => pathInfo.type !== 'array')
                      .map((pathInfo, index) => (
                        <option key={index} value={pathInfo.path}>
                          {pathInfo.path} ({pathInfo.type}) - {pathInfo.description}
                        </option>
                      ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    name="result_display_field"
                    value={formData.result_display_field}
                    onChange={handleInputChange}
                    placeholder="e.g., title, name (fetch sample to see options)"
                    style={{ width: '100%' }}
                  />
                )}
              </div>

              <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '1rem', marginBottom: 0 }}>
                Configure how to handle multiple results from the API. If multiple results path is set, 
                users will be prompted to select which result to use. The display field determines 
                what text is shown for each option.
              </p>
            </div>
          </div>

          {selectedInventoryType && (
            <div className="form-group">
              <FieldMappingBuilder
                fieldMappings={formData.field_mappings}
                onMappingsChange={(mappings) => setFormData(prev => ({ ...prev, field_mappings: mappings }))}
                sampleResponse={sampleResponse}
                inventoryFields={getInventoryTypeFields(selectedInventoryType)}
              />
            </div>
          )}

          {sampleResponse && (
            <div className="form-group">
              <label>Sample API Response Preview</label>
              <div style={{ 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                padding: '1rem',
                backgroundColor: '#f8f9fa',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                <div style={{ fontSize: '0.8rem', marginBottom: '0.5rem', color: '#666' }}>
                  Sample search: "{sampleSearchValue}"
                </div>
                <pre style={{ 
                  fontSize: '0.75rem', 
                  margin: 0, 
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {JSON.stringify(sampleResponse, null, 2)}
                </pre>
              </div>
            </div>
          )}

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                style={{ marginRight: '0.5rem' }}
              />
              Active
            </label>
          </div>

          {error && (
            <div style={{ 
              color: '#e74c3c', 
              backgroundColor: '#fdf2f2', 
              padding: '1rem', 
              borderRadius: '4px',
              marginBottom: '1rem'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn" disabled={loading}>
              {loading ? 'Saving...' : (agent ? 'Update Agent' : 'Create Agent')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CompletionAgentConfiguration;